import Sortable from "sortablejs";

type GameCard = HTMLElement & {
    criteria: string[];
    times_to_run: number;
};

type ScheduleTableSlot = HTMLElement & {
    provides: string[];
};

const errorBgStyle = "bg-error/25";
const errorTextStyle = "text-error";
const warningBgStyle = "bg-warning/25";
const successBgStyle = "bg-success/25";

const criterionMatches = (criterion: string, provides: string[]): boolean => {
    if (criterion.includes("|")) {
        // Split the criterion by "|" and check if any of the provides match
        const subCriteria = criterion.split("|");
        return subCriteria.some((subCriterion) => criterionMatches(subCriterion, provides));
    }
    // If the criterion starts with "!", it means it should not match
    let negated = criterion.startsWith("!");
    if (negated) {
        criterion = criterion.slice(1); // Remove the "!" for matching
        return !provides.includes(criterion);
    }
    return provides.includes(criterion);
};

const unmatchedCriteria = (criteria: string[], provides: string[]) => {
    // Check for the criteria that are not met by the provides
    // And return them as a string array
    return criteria.filter((criterion) => !criterionMatches(criterion, provides));
};

const clearGameCardDisplay = (gameCard: GameCard) => {
    // Clear the game card's display styles
    gameCard.classList.remove(errorBgStyle);
    gameCard.classList.remove(warningBgStyle);

    // Clear each element's criteria warnings
    Array.from(gameCard.querySelectorAll("[data-criteria-match]")).forEach((el) => {
        el.classList.remove(errorTextStyle);
    });
};

const event_manage_schedule = (scope_id: string) => {
    // Elements
    const scope = document.getElementById(scope_id);
    if (!scope) {
        console.error(`Scope with id ${scope_id} not found`);
        return;
    }
    const event = scope.dataset.event;
    if (!event) {
        console.error("Event ID not found in scope");
        return;
    }

    const unscheduledGamesElement = scope.querySelector(".unscheduled-games") as HTMLElement;
    const scheduleTableSlotElements = scope.querySelectorAll(".schedule-table-slot") as NodeListOf<ScheduleTableSlot>;
    const gameCardElements = scope.querySelectorAll(".game-card") as NodeListOf<GameCard>;
    const summaryElements = scope.querySelectorAll(".time-slot-summary") as NodeListOf<HTMLElement>;
    const saveButton = scope.querySelector(".save-button") as HTMLButtonElement;
    const commitButton = scope.querySelector(".commit-button") as HTMLButtonElement;
    const lastSavedInput = scope.querySelector("input[name='last-saved']") as HTMLInputElement;

    // Global state requiring functions
    const updateGameCardDisplay = (gameCard: GameCard, scheduleTableSlot: ScheduleTableSlot | HTMLElement) => {
        // If the scheduleTableSlot has undefined provides, it is the unscheduled games element - so nothing is unmatched
        const unmatched =
            !("provides" in scheduleTableSlot) || scheduleTableSlot.provides === undefined
                ? []
                : unmatchedCriteria(gameCard.criteria || [], scheduleTableSlot.provides || []);

        // Find out if there are more game cards scheduled with the same game
        const countScheduledGameCardsWithSameGame = Array.from(gameCardElements).filter(
            (card) => card.dataset.game === gameCard.dataset.game && card.parentElement !== unscheduledGamesElement,
        ).length;
        if (countScheduledGameCardsWithSameGame > gameCard.times_to_run) {
            unmatched.push(`times-to-run`);
        }

        clearGameCardDisplay(gameCard);

        if (unmatched.length !== 0) {
            // If not all criteria are met, show the game card with a warning or error style
            // If the unmatched criteria include a time slot or gm, use an error style
            const isError = unmatched.some(
                (criterion) =>
                    criterion.startsWith("time-slot-") || criterion.startsWith("!gm-") || criterion === "times-to-run",
            );
            gameCard.classList.add(isError ? errorBgStyle : warningBgStyle);

            // And find the elements with unmatched data-criteria attributes
            Array.from(gameCard.querySelectorAll("[data-criteria-match]"))
                .filter((el) => {
                    const criterion = el.getAttribute("data-criteria-match");
                    return criterion && unmatched.includes(criterion);
                })
                .forEach((el) => {
                    el.classList.add(errorTextStyle);
                });
        }
    };

    const emplaceCard = (
        gameCard: GameCard,
        fromElement: ScheduleTableSlot | HTMLElement,
        toElement: ScheduleTableSlot | HTMLElement,
    ) => {
        // Triggered on drag end OR on initialization if the game card is already in a slot (in which case fromElement is the unscheduledGamesElement and toElement is the slot)
        const gm = gameCard.dataset.gm as string;
        const playerCountMinimum = gameCard.dataset.playerCountMinimum
            ? parseInt(gameCard.dataset.playerCountMinimum, 10)
            : 0;
        const playerCountOptimum = gameCard.dataset.playerCountOptimum
            ? parseInt(gameCard.dataset.playerCountOptimum, 10)
            : 0;
        const playerCountMaximum = gameCard.dataset.playerCountMaximum
            ? parseInt(gameCard.dataset.playerCountMaximum, 10)
            : 0;

        const fromTimeSlot = fromElement.dataset.timeSlot;
        const toTimeSlot = toElement.dataset.timeSlot;

        // Add the gm to the provides of every schedule table slot in the column (with the same time slot) as the to element
        // EXCEPT the toElement itself
        if ("provides" in toElement) {
            // Find all the schedule table slots with the same time slot id
            const matchingSlots = Array.from(scheduleTableSlotElements).filter(
                (slot) => slot.dataset.timeSlot === toTimeSlot && slot !== toElement,
            ) as ScheduleTableSlot[];
            // Add the gm to the provides of each matching slot
            for (const slot of matchingSlots) {
                if (!slot.provides) {
                    // If provides is undefined, initialize it
                    slot.provides = [];
                }
                slot.provides.push(`gm-${gm}`);
                // If this slot contains a GameCard, update its display - as it may now be invalid
                const gameCardInSlot = slot.querySelector(".game-card") as GameCard | null;
                if (gameCardInSlot) {
                    updateGameCardDisplay(gameCardInSlot, slot);
                }
            }
        }

        // Remove the gm from the provides of every schedule table slot in the column (with the same time slot) as the from element
        // EXCEPT the fromElement itself
        if ("provides" in fromElement) {
            // Find all the schedule table slots with the same time slot id
            const matchingSlots = Array.from(scheduleTableSlotElements).filter(
                (slot) => slot.dataset.timeSlot === fromTimeSlot && slot !== fromElement,
            ) as ScheduleTableSlot[];
            // Remove the gm from the provides of each matching slot
            // Just remove one instance of the gm
            // This is to ensure that if the gm was added multiple times, it only gets removed once
            for (const slot of matchingSlots) {
                if (slot.provides) {
                    const index = slot.provides.indexOf(`gm-${gm}`);
                    if (index !== -1) {
                        slot.provides.splice(index, 1);
                        // If this slot contains a GameCard, update its display - as it may now be valid
                        const gameCardInSlot = slot.querySelector(".game-card") as GameCard | null;
                        if (gameCardInSlot) {
                            updateGameCardDisplay(gameCardInSlot, slot);
                        }
                    }
                }
            }
        }

        // Add the counts to the summary information with the same time slot id as the to element
        const toSummaryElement = Array.from(summaryElements).find((el) => el.dataset.timeSlot === toTimeSlot) as
            | HTMLElement
            | undefined;
        if (toSummaryElement) {
            // Update the summary spans within the summary element
            toSummaryElement.querySelectorAll(".summary-gm-count").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + 1).toString();
            });
            toSummaryElement.querySelectorAll(".summary-player-minimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountMinimum).toString();
            });
            toSummaryElement.querySelectorAll(".summary-player-optimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountOptimum).toString();
            });
            toSummaryElement.querySelectorAll(".summary-player-maximum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountMaximum).toString();
            });
            toSummaryElement.querySelectorAll(".summary-total-minimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountMinimum + 1).toString();
            });
            toSummaryElement.querySelectorAll(".summary-total-optimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountOptimum + 1).toString();
            });
            toSummaryElement.querySelectorAll(".summary-total-maximum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) + playerCountMaximum + 1).toString();
            });
        }

        // Subtract the counts from the summary information with the same time slot id as the from element
        const fromSummaryElement = Array.from(summaryElements).find((el) => el.dataset.timeSlot === fromTimeSlot) as
            | HTMLElement
            | undefined;
        if (fromSummaryElement) {
            // Update the summary spans within the summary element
            fromSummaryElement.querySelectorAll(".summary-gm-count").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - 1).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-player-minimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountMinimum).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-player-optimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountOptimum).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-player-maximum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountMaximum).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-total-minimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountMinimum - 1).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-total-optimum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountOptimum - 1).toString();
            });
            fromSummaryElement.querySelectorAll(".summary-total-maximum").forEach((el) => {
                el.textContent = (parseInt(el.textContent || "0", 10) - playerCountMaximum - 1).toString();
            });
        }

        // Update the game card's display based on the target slot
        updateGameCardDisplay(gameCard, toElement);
    };

    // Sortable
    const options: Sortable.Options = {
        group: "schedule-shared",
        animation: 150,
        emptyInsertThreshold: 0,
        delay: 0,
        forceFallback: true,
        forceAutoScrollFallback: true,
        onStart: (evt) => {
            // Check if the dragged item has a criteria property
            const criteria = (evt.item as GameCard).criteria;
            if (!criteria) {
                return;
            }

            // Highlight available time slots based on the dragged item's criteria
            for (const slot of scheduleTableSlotElements) {
                // Take a copy of the provides array to avoid modifying the original
                const provides = slot.provides ? [...slot.provides] : [];
                if (!provides) {
                    continue;
                }

                // Edge case - moving to the same time slot and already has a single instance of the gm
                // We can remove this provided gm - as it's created by THIS game card, so it might be valid to move it there (different table, same time slot)
                const gm = (evt.item as GameCard).dataset.gm;
                if (provides.includes(`gm-${gm}`) && slot.dataset.timeSlot === evt.from.dataset.timeSlot) {
                    // Remove the gm from the provides
                    const index = provides.indexOf(`gm-${gm}`);
                    if (index !== -1) {
                        provides.splice(index, 1);
                    }
                }

                // Check if the slot provides ALL of the criteria
                // If the criteria includes a "|", split it and check if any of the provides match
                const unmatched = unmatchedCriteria(criteria, provides);

                if (unmatched.length === 0) {
                    // No issues - success
                    slot.classList.add(successBgStyle);
                } else if (unmatched.some((criterion) => criterion.startsWith("time-slot-"))) {
                    // Issues in time-slot - error
                    slot.classList.add(errorBgStyle);
                    slot.dataset.issuesInfo = `${unmatched.length}: Time slot ${unmatched
                        .sort()
                        .filter((criterion) => !criterion.startsWith("time-slot-"))
                        .join(", ")}`;
                } else if (unmatched.some((criterion) => criterion.startsWith("!gm-"))) {
                    // Issues in gm - error
                    slot.classList.add(errorBgStyle);
                    slot.dataset.issuesInfo = `${unmatched.length}: GM Clash ${unmatched
                        .sort()
                        .filter((criterion) => !criterion.startsWith("!gm-"))
                        .join(", ")}`;
                } else {
                    // Other unmatched criteria - warning
                    slot.classList.add(warningBgStyle);
                    slot.dataset.issuesInfo = `${unmatched.length}: ${unmatched.sort().join(", ")}`;
                }
            }
        },
        onEnd: (evt) => {
            for (const el of scheduleTableSlotElements) {
                el.classList.remove(errorBgStyle);
                el.classList.remove(successBgStyle);
                el.classList.remove(warningBgStyle);
                delete el.dataset.issuesInfo; // Clear issues info
            }

            const gameCard = evt.item as GameCard;
            const fromElement = evt.from as ScheduleTableSlot | HTMLElement;
            const toElement = evt.to as ScheduleTableSlot | HTMLElement;
            emplaceCard(gameCard, fromElement, toElement);
        },
    };

    for (const slot of scheduleTableSlotElements) {
        Sortable.create(slot, options);
    }

    Sortable.create(unscheduledGamesElement, options);

    // Set the game cards criteria properties by JSON parsing the data-criteria attribute
    for (const gameCard of gameCardElements) {
        const criteriaString = gameCard.dataset.criteria;
        if (criteriaString) {
            try {
                const criteria = JSON.parse(criteriaString) as string[];
                gameCard.criteria = criteria;
            } catch (error) {
                console.error("Failed to parse criteria string:", criteriaString, error);
            }
        } else {
            console.warn("No criteria found for game card:", gameCard);
        }

        // Set the times_to_run property from the data-times-to-run attribute
        const timesToRunString = gameCard.dataset.timesToRun;
        if (timesToRunString) {
            const timesToRun = parseInt(timesToRunString, 10);
            if (!isNaN(timesToRun)) {
                gameCard.times_to_run = timesToRun;
            } else {
                console.warn("Invalid times_to_run value for game card:", gameCard, timesToRunString);
            }
        }
    }

    // Set the schedule table slot provides properties by JSON parsing the data-provides attribute
    for (const slot of scheduleTableSlotElements) {
        const providesString = slot.dataset.provides;
        if (providesString) {
            try {
                const provides = JSON.parse(providesString) as string[];
                slot.provides = provides;
            } catch (error) {
                console.error("Failed to parse provides string:", providesString, error);
            }
        } else {
            console.warn("No provides found for schedule table slot:", slot);
        }
    }

    // Emplace all game cards that started in their respective slots
    // Find all the game cards that are already in a slot (not in the unscheduled games element)
    for (const gameCard of gameCardElements) {
        const parentSlot = gameCard.closest(".schedule-table-slot") as ScheduleTableSlot | null;
        if (parentSlot) {
            // Emplace the card in the slot
            emplaceCard(gameCard, unscheduledGamesElement, parentSlot);
        }
    }

    const saveState = (commit: boolean) => {
        // Get the state - the game cards in the schedule table slots are the sessions to save
        const sessions = Array.from(gameCardElements)
            .map((gameCard) => {
                const game = gameCard.dataset.game;
                const timeSlot = (gameCard.closest(".schedule-table-slot") as HTMLElement)?.dataset.timeSlot;
                const table = (gameCard.closest(".schedule-table-slot") as HTMLElement)?.dataset.table;

                if (!game || !timeSlot || !table) {
                    return null;
                }

                return {
                    game: game,
                    time_slot: timeSlot,
                    table: table,
                };
            })
            .filter((session) => session !== null);
        console.log("Sessions to save:", sessions);

        const bodyJson = {
            sessions: sessions,
            commit: commit,
        };

        // PUT request to ./save endpoint
        // Fire and forget - no need to wait for the response
        fetch(window.location.href, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(bodyJson),
        }).then((response) => {
            if (!response.ok) {
                console.error("Failed to save schedule:", response.statusText);
            }
            lastSavedInput.value = new Date().toISOString(); // Update the last saved time
        });
    };

    // Add event listeners for save and commit buttons
    saveButton.addEventListener("click", () => {
        console.log("Save button clicked");
        if (confirm("Are you sure you want to save the currently displayed schedule?")) {
            saveState(false);
        }
    });

    commitButton.addEventListener("click", () => {
        console.log("Commit button clicked");
        if (confirm("Are you sure you want to COMMIT the currently displayed schedule?")) {
            saveState(true);
        }
    });
};

export default event_manage_schedule;
